export type PlanId = 'free' | 'basic' | 'premium' | 'pro';

export type PlanTerm = 'monthly' | 'quarterly' | 'yearly';

/**
 * Term pricing mirrors backend/services/stripe_billing_service.py::_get_price_id_for_plan:
 *  - quarterly: one upfront payment for 3 months of access, 5% off, no auto-renewal
 *  - yearly: recurring subscription at 10× monthly (2 months free)
 * Rounding is done in cents so the frontend shows exactly what Stripe charges.
 */
export function quarterlyPriceFor(monthly: number): number {
  return Math.round(monthly * 100 * 3 * 0.95) / 100;
}

export function yearlyPriceFor(monthly: number): number {
  return Math.round(monthly * 100 * 10) / 100;
}

export function getPlanPrice(plan: Pick<Plan, 'priceValue'>, term: PlanTerm): number {
  if (term === 'quarterly') return quarterlyPriceFor(plan.priceValue);
  if (term === 'yearly') return yearlyPriceFor(plan.priceValue);
  return plan.priceValue;
}

export function formatPlanPrice(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

export interface Plan {
  id: PlanId;
  name: string;
  price: string; // formatted price per month
  priceValue: number; // numeric
  features: string[];
  tokenLimit: number; // tokens per month
  storageGb: number;
  profilesIncluded: number;
  wellnessGoalsLimit: number | typeof Infinity; // wellness goals per profile
  geneticFilesIncluded: number; // genetic files included (per profile)
  geneticFileOneTimeFee?: number; // one-time fee per additional file
  overage: {
    tokensPer1k: number;
    storagePerGb: number;
  };
  canAddProfiles: boolean;
}

// Pro plan is defined but hidden from public `plans` until practitioner tier launches.
// To re-enable, add proPlan back into the plans array below.
const proPlan: Plan = {
  id: 'pro',
  name: 'Pro',
  price: '$49.95',
  priceValue: 49.95,
  features: [
    '~210 Coach conversations/mo (3M credits)',
    '50 GB storage',
    '10 profiles included + add-ons',
    'Personal health record management',
    'Wellness goal creation and management',
    'Unlimited free collaborators',
    'Up to 3 genetic files per profile (limited by storage). Max 1 VCF file or file 1GB or larger per profile',
    'Comprehensive genetic report (all 16 sections included)',
    'Compare genetic reports',
    'WGS/VCF processing support',
    'Continuous wellness coaching',
    'Custom genetic reports',
    'Wellness goals analytics',
    'Pro Dashboard for client management',
    'Custom CheckIn templates for structured goal tracking',
    'Coach Report Card for showcasing outcomes',
    'Practitioner Spotlight with newsletter features and Livewello Ads for top performers',
  ],
  tokenLimit: 3_000_000,
  storageGb: 50,
  profilesIncluded: 10,
  wellnessGoalsLimit: Infinity,
  geneticFilesIncluded: 3,
  overage: { tokensPer1k: 0.12, storagePerGb: 0.5 },
  canAddProfiles: true,
};

export const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    priceValue: 0,
    features: [
      '~10 Coach conversations/mo (150k credits)',
      '200 MB storage',
      '1 profile',
      '1 genetic data file',
      'Comprehensive genetic report (4 core sections: SNP Table, Health & Longevity, Nutrition & Metabolism, Detox & Environmental)',
      'Personal health record management',
      'Wellness goal creation and management',
      'Coach wellness coaching (limited by conversation credits)',
      'Unlimited free collaborators',
    ],
    tokenLimit: 150_000,
    storageGb: 0.2,
    profilesIncluded: 1,
    wellnessGoalsLimit: Infinity,
    geneticFilesIncluded: 1,
    overage: { tokensPer1k: 0, storagePerGb: 0 },
    canAddProfiles: false,
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '$12.95',
    priceValue: 12.95,
    features: [
      '~35 Coach conversations/mo (500k credits)',
      '5 GB storage',
      '3 profiles',
      'Personal health record management',
      'Wellness goal creation and management',
      'Unlimited free collaborators',
      'Up to 3 genetic files per profile (limited by storage). Max 1 VCF file or file 1GB or larger per profile',
      'Comprehensive genetic report (all 16 sections included)',
      'Compare genetic reports',
      'WGS/VCF processing support',
      'Continuous wellness coaching',
      'Custom genetic reports',
      'Wellness goals analytics',
    ],
    tokenLimit: 500_000,
    storageGb: 5,
    profilesIncluded: 3,
    wellnessGoalsLimit: Infinity,
    geneticFilesIncluded: 3, // 3 files per profile, limited by storage
    overage: { tokensPer1k: 0.12, storagePerGb: 0.5 },
    canAddProfiles: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$25',
    priceValue: 25,
    features: [
      '~70 Coach conversations/mo (1M credits)',
      '10 GB storage',
      '5 profiles included (+ add more at $25 each, one-time)',
      'Personal health record management',
      'Wellness goal creation and management',
      'Unlimited free collaborators',
      'Up to 3 genetic files per profile (limited by storage). Max 1 VCF file or file 1GB or larger per profile',
      'Comprehensive genetic report (all 16 sections included)',
      'Compare genetic reports',
      'WGS/VCF processing support',
      'Continuous wellness coaching',
      'Custom genetic reports',
      'Wellness goals analytics',
    ],
    tokenLimit: 1_000_000,
    storageGb: 10,
    profilesIncluded: 5,
    wellnessGoalsLimit: Infinity,
    geneticFilesIncluded: 3, // 3 files per profile, limited by storage
    overage: { tokensPer1k: 0.12, storagePerGb: 0.5 },
    canAddProfiles: true,
  },
];

export { proPlan };
